import { getReceiptDate } from "../../utils/receiptDate";
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  Container,
  Button,
  IconButton,
  Box,
  Typography,
  Paper,
  TextField,
  Snackbar,
  Alert,
  InputAdornment,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Base receipt template without items
const receiptTemplate = {
  id: null,
  soPhieu: "",
  ngay: new Date(),
  khachHang: "",
  trangThai: "Chờ xác nhận",
  tongSoLuong: 0,
  items: [],
};

const NewPhieuNhapBM = () => {
  const { id, action } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [storeName, setStoreName] = useState("");
  const [storeId, setStoreId] = useState("");
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeSearchTerm, setStoreSearchTerm] = useState(""); // Add store search state

  // New state variables for the custom product dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    maVt: "",
    ten: "",
    soLuong: "",
    slThucNhan: "",
    dvt: "cái",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  // New state variables for scroll navigation
  const [highlightedItems, setHighlightedItems] = useState([]);
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(-1);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [note, setNote] = useState(""); // Thêm state cho ghi chú

  // Ref for scrolling to bottom
  const bottomRef = useRef(null);

  // New state for showing scroll to bottom button
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);

  // Add new state for categories and products
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [originalItems, setOriginalItems] = useState([]); // Track original values

  // Function to fetch items from API
  const fetchItems = async () => {
    setApiLoading(true);
    setApiError(null);

    try {
      // Replace with your actual API endpoint
      const response = await fetch(
        "https://doticom.vn:3000/api/san-pham?IDCH=0&MaNLSP=BMKN"
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      // Define allowed product codes
      const allowedProductCodes = [
        "TH003",
        "CH014",
        "CH015",
        "CH016",
        "TH005",
        "TH006",
        "TH007",
        "TH008",
        "TH027",
        "TH046",
        "TH050",
        "TH028",
        "TH047",
        "TH035",
        "TH036",
        "TH051",
        "TH013",
        "TH052",
        "RBM",
        "DUAL",
        "XAL",
        "BDP",
        "BOPH",
        "CACHUA",
        "DAUAN",
        "DUONGTR",
        "HOTDCP",
        "JB2",
        "MUOITIEU",
        "PHMBCTH",
        "TGGA",
        "BT010",
        "MYNHO",
        "MYQUE",
        "MYBGL",
        "MYBGN",
        "BAOBM",
        "BMPMQ",
        "TAMT",
        "TGBM",
        "TGBMQ",
        "KHANUOT",
        "TH038",
      ];

      // Transform API data to the format we need and filter by allowed codes
      const transformedItems = data
        .filter((item) => allowedProductCodes.includes(item.MaSP))
        .map((item) => ({
          maVt: item.MaSP,
          ten: item.Ten_SP,
          soLuong: null,
          slThucNhan: null,
          dvt: item.DVT_SP, // We can store this additional info if needed
          giaMua: item.DonGia, // Updated to use DonGia from new API
        }));

      // Sort items by unit with "kg" priority
      const sortedItems = sortItemsByUnit(transformedItems);
      setItems(sortedItems);
      return sortedItems;
    } catch (error) {
      console.error("Error fetching items from API:", error);
      setApiError(error.message);
      // Return empty array in case of error
      return [];
    } finally {
      setApiLoading(false);
    }
  };

  // Function to fetch receipt details from API for editing
  const fetchReceiptDetails = async (maPN) => {
    try {
      const response = await fetch(
        `https://doticom.vn:3000/api/sale-nhap-hang-detail/${maPN}`
      );
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      console.log("📋 Receipt Details from API for editing:", data);

      // API trả về object với thuộc tính Items là array
      const receiptData = data[0];
      const items = receiptData.Items || [];
      console.log("📋 Items array from receipt details:", items);

      const mappedReceiptDetails = items.map((item) => ({
        maVt: item.MaSP,
        ten: item.TenSP,
        soLuong: item.SLNhapDau, // Số Lượng ban đầu
        slThucNhan: item.SLThucNhan, // SL Thực Nhận
        dvt: item.DVT || "cái",
        giaMua: item.GiaMua,
        giamPT: item.GiamPT || 0,
        thanhTien: item.ThanhTien,
        // Store original values for comparison
        originalSoLuong: item.SLNhapDau,
        originalSlThucNhan: item.SLThucNhan,
      }));

      console.log(
        "📋 Mapped Receipt Details for editing:",
        mappedReceiptDetails
      );

      // Return both items and store ID
      return {
        items: mappedReceiptDetails,
        storeId: receiptData.MaCH,
        ghiChu: receiptData.GhiChu || "",
      };
    } catch (error) {
      console.error("Error fetching receipt details for editing:", error);
      return {
        items: [],
        storeId: null,
        ghiChu: "",
      };
    }
  };

  // Add function to fetch store details by ID
  const fetchStoreById = async (storeId) => {
    try {
      const response = await fetch(`https://doticom.vn:3000/stores/${storeId}`);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const storeData = await response.json();
      return storeData;
    } catch (error) {
      console.error("Error fetching store details:", error);
      return null;
    }
  };

  // Function to fetch stores from API
  const fetchStores = async () => {
    try {
      const response = await fetch("https://doticom.vn:3000/stores");
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const storesData = await response.json();
      setStores(storesData);
      return storesData;
    } catch (error) {
      console.error("Error fetching stores:", error);
      setSnackbarMessage("Không thể tải danh sách cửa hàng");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return [];
    }
  };

  // Add function to fetch categories
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await fetch("https://doticom.vn:3000/categories");
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();

      const transformedCategories = data.map((cat) => ({
        id: cat.MaNLSP,
        name: cat.TenLoaiSP,
      }));

      setCategories(transformedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setSnackbarMessage("Không thể tải danh mục sản phẩm");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Add function to fetch products by category
  const fetchProductsByCategory = async (categoryId) => {
    if (!categoryId) return;

    setLoadingProducts(true);
    try {
      const response = await fetch(
        `https://doticom.vn:3000/items?category=${categoryId}`
      );
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();

      const transformedProducts = data.map((product) => ({
        id: product.MaSP,
        name: product.Ten_SP,
        price: parseFloat(product.DonGia) || 0,
        price2: parseFloat(product.DonGia2) || 0,
        unit: product.DVT_SP || "",
      }));

      setProducts((prev) => ({
        ...prev,
        [categoryId]: transformedProducts,
      }));
    } catch (error) {
      console.error("Error fetching products:", error);
      setSnackbarMessage("Không thể tải sản phẩm");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    // Check for admin privileges
    try {
      const adminStatus = localStorage.getItem("isAdmin") === "true";
      setIsAdmin(adminStatus);
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
    }

    // Prevent wheel events on number inputs by unfocusing them
    const preventWheelChange = (e) => {
      // Check if the focused element is a number input
      if (document.activeElement.type === "number") {
        document.activeElement.blur();
      }
    };

    // Add event listeners
    document.addEventListener("wheel", preventWheelChange, { passive: true });

    // In a real app, fetch the receipt data from an API
    const initializeReceipt = async () => {
      setLoading(true);

      // Fetch stores list first
      await fetchStores();

      // Check if we're editing an existing receipt
      if (id && action === "edit") {
        try {
          // Fetch items from API first and receipt details
          const [fixedItems, receiptDetailsData] = await Promise.all([
            fetchItems(),
            fetchReceiptDetails(id),
          ]);

          const {
            items: receiptDetails,
            storeId: receiptStoreId,
            ghiChu,
          } = receiptDetailsData;

          // Fetch store information using the store ID from receipt
          let storeInfo = null;
          if (receiptStoreId) {
            storeInfo = await fetchStoreById(receiptStoreId);
          }

          // Set store information if found
          if (storeInfo) {
            setStoreName(storeInfo.TenCuaHang);
            setCustomerName(storeInfo.TenCuaHang);
            setStoreId(receiptStoreId.toString());
          }

          // Merge fixed items with receipt details by matching product codes
          const mergedItems = fixedItems.map((fixedItem) => {
            // Find matching item in receipt details by comparing MaSP
            const detailItem = receiptDetails.find(
              (item) => item.maVt === fixedItem.maVt
            );

            if (detailItem) {
              // If found in receipt details, merge the data
              const merged = {
                ...fixedItem,
                soLuong: detailItem.soLuong, // Use receipt detail quantities
                slThucNhan: detailItem.slThucNhan,
                ten: detailItem.ten || fixedItem.ten,
                dvt: detailItem.dvt || fixedItem.dvt,
                giaMua: detailItem.giaMua || fixedItem.giaMua,
                giamPT: detailItem.giamPT || 0,
                thanhTien: detailItem.thanhTien || 0,
                // Store original values for change tracking
                originalSoLuong: detailItem.originalSoLuong,
                originalSlThucNhan: detailItem.originalSlThucNhan,
              };
              return merged;
            } else {
              // If not found in receipt details, keep the fixed item as is with null originals
              return {
                ...fixedItem,
                originalSoLuong: null,
                originalSlThucNhan: null,
              };
            }
          });

          // Also add any items from receipt details that don't exist in fixed items
          const additionalItems = receiptDetails.filter(
            (detailItem) =>
              !fixedItems.some(
                (fixedItem) => fixedItem.maVt === detailItem.maVt
              )
          );

          // Combine merged items with additional items
          const finalItems = [...mergedItems, ...additionalItems];

          // Sort the final items by unit with "kg" priority
          const sortedFinalItems = sortItemsByUnit(finalItems);

          // Store original items for change tracking
          setOriginalItems(JSON.parse(JSON.stringify(sortedFinalItems)));

          // Generate receipt number for editing
          const editReceiptNumber = `DT/BN-${String(id).padStart(7, "0")}`;

          setReceipt({
            ...receiptTemplate,
            id: parseInt(id),
            soPhieu: editReceiptNumber,
            khachHang: storeInfo?.TenCuaHang || "",
            items: sortedFinalItems,
            trangThai: "Chờ cập nhật",
            ghiChu: ghiChu,
          });
          setNote(ghiChu); // Set note khi edit
          setReceiptNumber(editReceiptNumber);
          setLoading(false);
          return;
        } catch (error) {
          console.error("Error loading receipt for editing:", error);
          setApiError("Không thể tải dữ liệu phiếu nhập để chỉnh sửa");
          setLoading(false);
          return;
        }
      }

      // For new receipts, don't auto-set store info from localStorage
      // User must select from dropdown

      // Fetch items from API for new receipt
      const itemsFromApi = await fetchItems();

      // Sort items for new receipt
      const sortedItemsFromApi = sortItemsByUnit(itemsFromApi);

      // Generate a unique receipt number for new receipts
      const nextId = generateNextReceiptId();
      const nextReceiptNumber = `DT/BN-${String(nextId).padStart(7, "0")}`;

      // Create a new receipt with items from API but no store selected initially
      setReceipt({
        ...receiptTemplate,
        ngay: new Date(),
        id: nextId,
        soPhieu: nextReceiptNumber,
        khachHang: "",
        items: sortedItemsFromApi,
        ghiChu: "",
      });
      setNote(""); // Reset note khi tạo mới
      setReceiptNumber(nextReceiptNumber);
      setLoading(false);
    };

    initializeReceipt();

    // Cleanup function
    return () => {
      document.removeEventListener("wheel", preventWheelChange);
    };
  }, [id, action]);

  // Generate next receipt ID based on existing receipts in localStorage
  const generateNextReceiptId = () => {
    try {
      const existingReceipts =
        JSON.parse(localStorage.getItem("phieuNhapList")) || [];
      if (existingReceipts.length === 0) {
        return 1;
      }

      const maxId = Math.max(...existingReceipts.map((receipt) => receipt.id));
      return maxId + 1;
    } catch (error) {
      console.error("Error generating receipt ID:", error);
      return Math.floor(Math.random() * 10000);
    }
  };

  // Helper function to check if an item has changed
  const hasItemChanged = (currentItem, originalItem) => {
    if (!originalItem) return false; // New item, not a change

    const currentSoLuong = parseFloat(currentItem.soLuong) || 0;
    const originalSoLuong = parseFloat(originalItem.originalSoLuong) || 0;
    const currentSlThucNhan = parseFloat(currentItem.slThucNhan) || 0;
    const originalSlThucNhan = parseFloat(originalItem.originalSlThucNhan) || 0;

    return (
      currentSoLuong !== originalSoLuong ||
      currentSlThucNhan !== originalSlThucNhan
    );
  };

  // Helper function to check if an item was deleted (had quantity before, now has 0 or empty)
  const isItemDeleted = (currentItem, originalItem) => {
    if (!originalItem) return false;

    const currentSoLuong = parseFloat(currentItem.soLuong) || 0;
    const originalSoLuong = parseFloat(originalItem.originalSoLuong) || 0;

    return originalSoLuong > 0 && currentSoLuong === 0;
  };

  // Function to update existing receipt via API
  const updateReceiptViaAPI = async () => {
    try {
      // Get only items that have changed or been deleted
      const changedItems = [];
      const deletedItems = [];

      receipt.items.forEach((currentItem) => {
        // Find the original item to compare
        const originalItem = originalItems.find(
          (origItem) => origItem.maVt === currentItem.maVt
        );

        if (hasItemChanged(currentItem, originalItem)) {
          changedItems.push(currentItem);
        } else if (isItemDeleted(currentItem, originalItem)) {
          deletedItems.push(currentItem);
        }
      });

      // Also check for newly added items (items that don't exist in originalItems)
      const newItems = receipt.items.filter((currentItem) => {
        const hasOriginal = originalItems.some(
          (origItem) => origItem.maVt === currentItem.maVt
        );
        const hasQuantity = parseFloat(currentItem.soLuong) > 0;
        return !hasOriginal && hasQuantity;
      });

      // Combine all items that need to be sent to API
      const itemsToUpdate = [...changedItems, ...deletedItems, ...newItems];

      console.log("📝 Changed items:", changedItems.length);
      console.log("🗑️ Deleted items:", deletedItems.length);
      console.log("➕ New items:", newItems.length);
      console.log("🔄 Total items to update:", itemsToUpdate.length);

      // If no changes, show message
      if (itemsToUpdate.length === 0) {
        setSnackbarMessage("Không có thay đổi nào để cập nhật!");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
        return;
      }

      // Format the request body according to API requirements
      const requestBody = {
        Items: itemsToUpdate.map((item) => {
          const currentQuantity = parseFloat(item.soLuong) || 0;
          const isDeleted = currentQuantity === 0;

          return {
            MaSP: item.maVt,
            TenSP: item.ten,
            SoLuong: 0,
            SLThucNhan: isDeleted ? 0 : parseFloat(item.slThucNhan) || 0,
            SoLuongNhapDau: currentQuantity, // This will be 0 for deleted items
            GiaMua: isDeleted ? 0 : item.giaMua || 0,
            GiamPT: isDeleted ? 0 : item.giamPT || 0,
            ThanhTien: isDeleted ? 0 : currentQuantity * (item.giaMua || 0),
          };
        }),
        GhiChu: note || "", // Thêm ghi chú vào body
      };

      console.log("🔄 Updating receipt with ID:", id);
      console.log("🔄 Request body:", requestBody);

      // Call the update API
      const response = await fetch(
        `https://doticom.vn:3000/api/sale-nhap-hang/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update receipt");
      }

      const data = await response.json();
      setSnackbarMessage(
        `Cập nhật phiếu nhập thành công! (${itemsToUpdate.length} sản phẩm được cập nhật)`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Wait a moment before redirecting
      setTimeout(() => {
        navigate("/nhaphangbm");
      }, 2000);
    } catch (error) {
      setSnackbarMessage(
        `Có lỗi xảy ra khi cập nhật phiếu nhập: ${error.message}`
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Error updating receipt via API:", error);
    }
  };

  // Function to post import items to the API (for new receipts)
  const postImportItems = async () => {
    try {
      // Check if store is selected for new receipts
      if (!storeId) {
        setSnackbarMessage("Vui lòng chọn cửa hàng trước khi gửi phiếu!");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // Filter only items that have a quantity value (using soLuong instead of slThucNhan)
      const itemsToImport = receipt.items.filter(
        (item) => item.soLuong && parseFloat(item.soLuong) > 0
      );

      if (itemsToImport.length === 0) {
        setSnackbarMessage("Không có sản phẩm nào để nhập!");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // Format the request body according to API requirements
      const requestBody = {
        phieu: {
          MaKH: storeId.toString(),
          NgayNhap: getReceiptDate(),
          TenUser: localStorage.getItem("TenUser") || "User",
          GhiChu: note || `Phiếu nhập nguyên liệu ${receipt.soPhieu}`, // Sử dụng note
          MaCH: storeId.toString(),
          LoaiPhieu: 79, // 79 = Nhập banh mi
        },
        chitiet: itemsToImport.map((item) => ({
          MaSP: item.maVt,
          TenSP: item.ten,
          SoLuong: 0,
          SoLuongTN: parseFloat(item.slThucNhan) || 0,
          SoLuongNhapDau: parseFloat(item.soLuong),
          GiaMua: item.giaMua || 0,
          GiamPT: 0, // Default discount percentage
          ThanhTien: parseFloat(item.soLuong) * (item.giaMua || 0),
        })),
      };

      // Call the API
      const response = await fetch("https://doticom.vn:3000/nhap-hang", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API error: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();
      setSnackbarMessage(
        "Phiếu nhập đã được lưu vào cơ sở dữ liệu thành công!"
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Wait a moment before redirecting
      setTimeout(() => {
        navigate("/nhaphangbm");
      }, 2000);
    } catch (error) {
      setSnackbarMessage(
        `Có lỗi xảy ra khi lưu phiếu nhập vào cơ sở dữ liệu: ${error.message}`
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Error saving receipt to API:", error);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handlePrint = () => {
    window.print();
  };

  // Remove handleCustomerNameChange function as it's no longer needed

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value.toLowerCase();
    setSearchTerm(newSearchTerm);

    // Update highlighted items when search term changes
    if (newSearchTerm && receipt && receipt.items) {
      const matchingItems = receipt.items
        .map((item, index) => {
          const maVtMatch =
            item.maVt && item.maVt.toLowerCase().includes(newSearchTerm);
          const tenMatch =
            item.ten && item.ten.toLowerCase().includes(newSearchTerm);

          if (maVtMatch || tenMatch) {
            return { item, index };
          }
          return null;
        })
        .filter(Boolean);

      setHighlightedItems(matchingItems);
      setCurrentHighlightIndex(matchingItems.length > 0 ? 0 : -1);
      setShowScrollButtons(matchingItems.length > 1);

      // Auto scroll to first result
      if (matchingItems.length > 0) {
        setTimeout(() => scrollToHighlightedItem(0), 100);
      }
    } else {
      setHighlightedItems([]);
      setCurrentHighlightIndex(-1);
      setShowScrollButtons(false);
    }
  };

  // Function to scroll to a highlighted item
  const scrollToHighlightedItem = (highlightIndex) => {
    if (highlightIndex < 0 || highlightIndex >= highlightedItems.length) return;

    const itemIndex = highlightedItems[highlightIndex].index;
    const tableRow = document.querySelector(
      `tr[data-item-index="${itemIndex}"]`
    );

    if (tableRow) {
      tableRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      // Add temporary pulse animation
      tableRow.classList.add("scroll-highlight");
      setTimeout(() => {
        tableRow.classList.remove("scroll-highlight");
      }, 1000);
    }
  };

  // Function to scroll to next highlighted item
  const scrollToNext = () => {
    if (highlightedItems.length === 0) return;

    const nextIndex = (currentHighlightIndex + 1) % highlightedItems.length;
    setCurrentHighlightIndex(nextIndex);
    scrollToHighlightedItem(nextIndex);
  };

  // Function to scroll to previous highlighted item
  const scrollToPrevious = () => {
    if (highlightedItems.length === 0) return;

    const prevIndex =
      currentHighlightIndex === 0
        ? highlightedItems.length - 1
        : currentHighlightIndex - 1;
    setCurrentHighlightIndex(prevIndex);
    scrollToHighlightedItem(prevIndex);
  };

  // Function to highlight matching items in the table
  const highlightMatchingItems = (item, index) => {
    if (!searchTerm) return false;

    const maVtMatch = item.maVt && item.maVt.toLowerCase().includes(searchTerm);
    const tenMatch = item.ten && item.ten.toLowerCase().includes(searchTerm);

    return maVtMatch || tenMatch;
  };

  const confirmAction = (button, message, isBqdAction = false) => {
    if (button.classList.contains("confirmed")) {
      return;
    }

    // Check if store is selected for new receipts
    if (!id && !storeId) {
      setSnackbarMessage("Vui lòng chọn cửa hàng trước khi xác nhận!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!receipt.khachHang) {
      setSnackbarMessage("Vui lòng nhập tên khách hàng");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // For edit mode, check if there are any changes
    if (id && action === "edit") {
      const hasAnyChanges = receipt.items.some((currentItem) => {
        const originalItem = originalItems.find(
          (origItem) => origItem.maVt === currentItem.maVt
        );
        return (
          hasItemChanged(currentItem, originalItem) ||
          isItemDeleted(currentItem, originalItem) ||
          (!originalItem && parseFloat(currentItem.soLuong) > 0)
        ); // New items
      });

      if (!hasAnyChanges) {
        setSnackbarMessage("Không có thay đổi nào để cập nhật!");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
        return;
      }
    }

    // Calculate total quantity using soLuong instead of slThucNhan
    let totalQty = 0;
    const updatedItems = receipt.items.map((item) => {
      if (item.soLuong) {
        const qty = parseFloat(item.soLuong);
        if (!isNaN(qty)) {
          totalQty += qty;
        }
      }
      return item;
    });

    if (totalQty === 0 && !id) {
      // Only check for new receipts
      setSnackbarMessage(
        "Vui lòng nhập số lượng ban đầu cho ít nhất một sản phẩm"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    button.textContent = "✓ Đã xác nhận";
    button.classList.add("confirmed");

    const statusDiv = button.nextElementSibling;
    statusDiv.textContent = message;
    statusDiv.style.color = "#2196F3";
    statusDiv.style.fontWeight = "bold";

    // Add timestamp
    const now = new Date();
    const timestamp = now.toLocaleString("vi-VN");
    statusDiv.textContent = message + "\n" + timestamp;

    // Determine the new status based on the action and previous status
    const newTrangThai = isBqdAction ? "Đã duyệt" : "Đã gửi phiếu";

    // Update receipt state
    const updatedReceipt = {
      ...receipt,
      items: updatedItems,
      tongSoLuong: totalQty,
      trangThai: newTrangThai,
      ngayXacNhan: now,
      idCuaHang: storeId,
      bqdApproved: isBqdAction ? true : false,
      bqdTimestamp: isBqdAction ? now : null,
      bqdMessage: isBqdAction ? message : null,
      lastUpdated: now,
      updateCount: action === "edit" ? (receipt.updateCount || 0) + 1 : 1,
    };

    setReceipt(updatedReceipt);

    // Call appropriate API based on whether we're editing or creating
    if (id && action === "edit") {
      updateReceiptViaAPI();
    } else {
      postImportItems();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Add function to handle deleting a product (reset quantity to 0)
  const handleDeleteProduct = (itemIndex) => {
    const newItems = [...receipt.items];
    newItems[itemIndex] = {
      ...newItems[itemIndex],
      soLuong: "",
      slThucNhan: "",
    };
    setReceipt({ ...receipt, items: newItems });

    setSnackbarMessage("Đã xóa số lượng sản phẩm");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  // Handle opening the add product dialog
  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    // Reset the form
    setNewProduct({
      maVt: "",
      ten: "",
      soLuong: "",
      slThucNhan: "",
      dvt: "cái",
    });
    setSelectedCategory("");
    setSelectedProduct("");
    setFormErrors({});

    // Fetch categories when opening dialog
    if (categories.length === 0) {
      fetchCategories();
    }
  };

  // Handle closing the add product dialog
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  // Handle input changes in the add product form
  const handleNewProductChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setSelectedProduct("");

    // Reset product info
    setNewProduct((prev) => ({
      ...prev,
      maVt: "",
      ten: "",
    }));

    // Fetch products for selected category
    if (categoryId && !products[categoryId]) {
      fetchProductsByCategory(categoryId);
    }
  };

  // Handle product selection
  const handleProductChange = (e) => {
    const productId = e.target.value;
    setSelectedProduct(productId);

    if (productId && selectedCategory) {
      const selectedProductData = products[selectedCategory]?.find(
        (p) => p.id === productId
      );
      if (selectedProductData) {
        setNewProduct((prev) => ({
          ...prev,
          maVt: selectedProductData.id,
          ten: selectedProductData.name,
          dvt: selectedProductData.unit || "cái",
        }));
      }
    }
  };

  // Validate the form before submission
  const validateForm = () => {
    const errors = {};

    if (!selectedCategory) {
      errors.category = "Vui lòng chọn danh mục";
    }

    if (!selectedProduct) {
      errors.product = "Vui lòng chọn sản phẩm";
    }

    if (!newProduct.soLuong || parseFloat(newProduct.soLuong) <= 0) {
      errors.soLuong = "Vui lòng nhập số lượng hợp lệ";
    }

    // In NewPhieuNhapBM, don't validate slThucNhan (always hidden)

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle adding the new product to the receipt
  const handleAddNewProduct = () => {
    if (!validateForm()) {
      return;
    }

    // Check if the product code already exists
    const existingProduct = receipt.items.find(
      (item) => item.maVt === newProduct.maVt
    );
    if (existingProduct) {
      setSnackbarMessage("Sản phẩm với mã này đã tồn tại trong phiếu.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    // Add the new product to the receipt
    const updatedItems = [
      ...receipt.items,
      {
        maVt: newProduct.maVt,
        ten: newProduct.ten,
        soLuong: newProduct.soLuong || null,
        slThucNhan: newProduct.slThucNhan || null,
        dvt: newProduct.dvt,
        isCustom: true, // Mark as a custom-added product
      },
    ];

    // Sort the updated items by unit with "kg" priority
    const sortedUpdatedItems = sortItemsByUnit(updatedItems);

    setReceipt({
      ...receipt,
      items: sortedUpdatedItems,
    });

    // Show success message
    setSnackbarMessage("Đã thêm sản phẩm mới vào phiếu.");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);

    // Close the dialog
    handleCloseAddDialog();
  };

  // Function to handle store selection
  const handleStoreSelect = (event) => {
    const selectedStoreId = event.target.value;
    const selectedStore = stores.find(
      (store) => store.IDCH === selectedStoreId
    );

    if (selectedStore) {
      setStoreId(selectedStoreId);
      setStoreName(selectedStore.TenCuaHang);
      setCustomerName(selectedStore.TenCuaHang);

      // Update receipt with selected store info
      if (receipt) {
        setReceipt({
          ...receipt,
          khachHang: selectedStore.TenCuaHang,
        });
      }
    }
  };

  // Add function to filter stores based on search term
  const getFilteredStores = () => {
    if (!storeSearchTerm) return stores;

    return stores.filter(
      (store) =>
        store.TenCuaHang.toLowerCase().includes(
          storeSearchTerm.toLowerCase()
        ) || store.IDCH.toString().includes(storeSearchTerm)
    );
  };

  useEffect(() => {
    // Theo dõi cuộn để hiện nút cuộn xuống cuối trang
    const handleScroll = () => {
      // Hiện nút nếu có receipt và có nhiều sản phẩm
      if (receipt && receipt.items && receipt.items.length > 10) {
        // Nếu chưa ở gần cuối trang thì hiện nút
        const scrollY = window.scrollY || window.pageYOffset;
        const windowHeight = window.innerHeight;
        const docHeight = document.documentElement.scrollHeight;
        if (docHeight - (scrollY + windowHeight) > 120) {
          setShowScrollToBottom(true);
        } else {
          setShowScrollToBottom(false);
        }
      } else {
        setShowScrollToBottom(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [receipt]);

  // Add function to sort items by unit with "kg" priority
  const sortItemsByUnit = (items) => {
    return [...items].sort((a, b) => {
      const unitA = (a.dvt || "").toLowerCase();
      const unitB = (b.dvt || "").toLowerCase();

      // Prioritize "kg" first
      if (unitA === "kg" && unitB !== "kg") {
        return -1;
      }
      if (unitB === "kg" && unitA !== "kg") {
        return 1;
      }

      // If both are "kg" or neither is "kg", sort alphabetically by unit
      if (unitA < unitB) {
        return -1;
      }
      if (unitA > unitB) {
        return 1;
      }

      // If units are the same, sort by product name
      const nameA = (a.ten || "").toLowerCase();
      const nameB = (b.ten || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  if (loading || apiLoading) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>Đang tải dữ liệu phiếu nhập...</Typography>
      </Container>
    );
  }

  if (apiError) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          Có lỗi khi tải dữ liệu sản phẩm: {apiError}.
          <Button size="small" onClick={fetchItems} sx={{ ml: 2 }}>
            Thử lại
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!receipt) {
    return (
      <Container maxWidth="lg">
        <Typography>Không tìm thấy phiếu nhập</Typography>
      </Container>
    );
  }

  // Group items into columns for table layout (vertical first, then horizontal)
  const organizeItemsIntoColumns = () => {
    if (!receipt || !receipt.items) return [];

    // Number of columns we want to display
    const numColumns = 3;

    // Calculate how many items will be in each column
    const itemsPerColumn = Math.ceil(receipt.items.length / numColumns);

    // Create the columns structure
    const columns = [];
    for (let col = 0; col < numColumns; col++) {
      columns.push([]);
    }

    // Fill columns with items (vertical first, then horizontal)
    receipt.items.forEach((item, index) => {
      // Calculate which column this item belongs to
      const columnIndex = Math.floor(index / itemsPerColumn);

      // If we've exceeded our columns, don't add it
      if (columnIndex < numColumns) {
        columns[columnIndex].push(item);
      }
    });

    // Make sure all columns have the same number of rows by adding empty items
    // This ensures our table layout doesn't break
    for (let col = 0; col < columns.length; col++) {
      while (columns[col].length < itemsPerColumn) {
        columns[col].push({ maVt: "", ten: "", soLuong: "", slThucNhan: null });
      }
    }

    return columns;
  };

  // Replace the existing chunkItems variable with organizedColumns
  const organizedColumns = organizeItemsIntoColumns();

  // Format date for display
  const day = receipt.ngay.getDate();
  const month = receipt.ngay.getMonth() + 1;
  const year = receipt.ngay.getFullYear();

  const pageTitle =
    id && action === "edit" ? "Chỉnh Sửa Phiếu Nhập" : "Thêm Phiếu Nhập Mới";

  // Add function to get the current status text for display
  const getReceiptStatus = () => {
    if (!receipt) return "Đã gửi phiếu";

    if (receipt.trangThai === "Hoàn thành") {
      return "Hoàn thành";
    }

    // Check if it's been approved by BQD
    const isBqdApproved = receipt.bqdApproved || false;
    return isBqdApproved ? "Đã duyệt" : "Đã gửi phiếu";
  };

  // Add function to open preview
  const handleOpenPreview = () => {
    // Copy current items to preview state, filtering only items with quantities
    const itemsWithQuantity = receipt.items.filter(
      (item) => item.soLuong && parseFloat(item.soLuong) > 0
    );
    setPreviewItems(JSON.parse(JSON.stringify(itemsWithQuantity)));
    setOpenPreviewDialog(true);
  };

  // Add function to close preview
  const handleClosePreview = () => {
    setOpenPreviewDialog(false);
  };

  // Add function to save preview changes
  const handleSavePreview = () => {
    // Update receipt with preview items
    setReceipt({
      ...receipt,
      items: previewItems,
    });
    setSnackbarMessage("Đã lưu thay đổi từ xem trước");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setOpenPreviewDialog(false);
  };

  // Add function to handle preview item changes
  const handlePreviewItemChange = (index, field, value) => {
    const newPreviewItems = [...previewItems];
    newPreviewItems[index] = {
      ...newPreviewItems[index],
      [field]: value,
    };
    setPreviewItems(newPreviewItems);
  };

  // Add function to organize preview items into columns
  const organizePreviewItemsIntoColumns = () => {
    if (!previewItems || previewItems.length === 0) return [];

    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      return [previewItems];
    }

    const numColumns = 3;
    const itemsPerColumn = Math.ceil(previewItems.length / numColumns);
    const columns = [];
    
    for (let col = 0; col < numColumns; col++) {
      columns.push([]);
    }

    previewItems.forEach((item, index) => {
      const columnIndex = Math.floor(index / itemsPerColumn);
      if (columnIndex < numColumns) {
        columns[columnIndex].push(item);
      }
    });

    for (let col = 0; col < columns.length; col++) {
      while (columns[col].length < itemsPerColumn) {
        columns[col].push({ maVt: "", ten: "", soLuong: "", slThucNhan: null });
      }
    }

    return columns;
  };

  return (
    <Box>
      {/* Header actions - mobile optimized */}
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1, sm: 0 },
          "@media print": { display: "none" },
        }}
      >
        <Box display="flex" alignItems="center" sx={{ mb: { xs: 1, sm: 0 } }}>
          <IconButton
            onClick={handleGoBack}
            sx={{ mr: 1, p: { xs: 1, sm: 1.5 } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="h1"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {pageTitle}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<VisibilityIcon />}
            onClick={handleOpenPreview}
            variant="outlined"
            color="secondary"
            size="small"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            Xem trước
          </Button>
          <Button
            startIcon={<EditIcon />}
            variant="outlined"
            size="small"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            Sửa
          </Button>
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            variant="contained"
            color="primary"
            size="small"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            In
          </Button>
        </Box>
      </Box>

      {/* Store selection section - only show for new receipts */}
      {!id && (
        <Box
          sx={{
            p: { xs: 1, sm: 2 },
            mb: 2,
            "@media print": { display: "none" },
          }}
        >
          <Paper sx={{ p: 2 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              Chọn cửa hàng
            </Typography>

            {/* Store search input */}
            <TextField
              fullWidth
              size="small"
              label="Tìm kiếm cửa hàng"
              placeholder="Nhập tên cửa hàng hoặc mã ID..."
              value={storeSearchTerm}
              onChange={(e) => setStoreSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="store-select-label">Chọn cửa hàng</InputLabel>
              <Select
                labelId="store-select-label"
                value={storeId}
                label="Chọn cửa hàng"
                onChange={handleStoreSelect}
                required
              >
                <MenuItem value="">
                  <em>-- Chọn cửa hàng --</em>
                </MenuItem>
                {getFilteredStores().map((store) => (
                  <MenuItem key={store.IDCH} value={store.IDCH}>
                    {store.TenCuaHang} ({store.IDCH})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Show filtered results count */}
            {storeSearchTerm && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Tìm thấy {getFilteredStores().length} cửa hàng
              </Typography>
            )}

            {!storeId && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                * Vui lòng chọn cửa hàng để tiếp tục
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {/* Store information indicator - mobile optimized */}
      {storeName && (
        <Box
          sx={{
            p: { xs: 1, sm: 1 },
            mx: { xs: 1, sm: 0 },
            mb: 2,
            backgroundColor: "#e3f2fd",
            display: "flex",
            alignItems: "center",
            borderRadius: 1,
            "@media print": { display: "none" },
          }}
        >
          <StorefrontIcon
            sx={{
              mr: 1,
              color: "#1976d2",
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          />
          <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            Cửa hàng: <strong>{storeName}</strong>
            {storeId && <span> (ID: {storeId})</span>}
          </Typography>
        </Box>
      )}

      {/* Search bar - sticky, always on top when scrolling */}
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          mb: { xs: 2, sm: 3 },
          "@media print": { display: "none" },
          position: "sticky",
          top: 0,
          backgroundColor: "white",
          zIndex: 1201,
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          borderRadius: "0 0 8px 8px",
          transition: "box-shadow 0.3s ease",
        }}
      >
        {/* Chỉnh sửa ghi chú phiếu nhập */}
        {(storeId || (id && action === "edit")) && (
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Ghi chú phiếu nhập"
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (receipt) setReceipt({ ...receipt, ghiChu: e.target.value });
              }}
              size="small"
              sx={{
                mb: 1,
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                },
              }}
              placeholder="Nhập ghi chú cho phiếu nhập (nếu có)"
            />
          </Box>
        )}

        {/* Only show search if store is selected for new receipts, or always show for edit mode */}
        {(storeId || (id && action === "edit")) && (
          <Box sx={{ position: "relative" }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Tìm kiếm sản phẩm"
              placeholder="Nhập mã sản phẩm..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
              }}
            />

            {/* Search results counter and navigation */}
            {showScrollButtons && (
              <Box
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  borderRadius: 1,
                  padding: "4px 8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.75rem", minWidth: "40px" }}
                >
                  {currentHighlightIndex + 1}/{highlightedItems.length}
                </Typography>
                <IconButton
                  size="small"
                  onClick={scrollToPrevious}
                  sx={{
                    p: 0.5,
                    "& .MuiSvgIcon-root": { fontSize: "1rem" },
                  }}
                  title="Kết quả trước"
                >
                  <ArrowBackIcon sx={{ transform: "rotate(90deg)" }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={scrollToNext}
                  sx={{
                    p: 0.5,
                    "& .MuiSvgIcon-root": { fontSize: "1rem" },
                  }}
                  title="Kết quả tiếp theo"
                >
                  <ArrowBackIcon sx={{ transform: "rotate(-90deg)" }} />
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        {/* Message when no store is selected for new receipts */}
        {!storeId && !id && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Vui lòng chọn cửa hàng ở trên để bắt đầu tạo phiếu nhập
          </Alert>
        )}
      </Box>

      {/* Receipt layout - mobile optimized - only show if store is selected or in edit mode */}
      {(storeId || (id && action === "edit")) && (
        <div className="receipt">
          <div className="header">
            <div className="logo">
              DOTI
              <br />
              COM
            </div>
            <div className="title">PHIẾU GIAO NHẬN HÀNG HÓA</div>
            <div className="subtitle">20 20 30 90 96</div>
          </div>

          <div className="info-section">
            <div className="info-left">
              <div className="customer-row">
                <span className="customer-label">Khách hàng:</span>
                <div className="customer-display handwritten">
                  {receipt.khachHang || "Chưa chọn cửa hàng"}
                </div>
              </div>
              <div className="date-row">
                Ngày: <span className="handwritten">{day}</span> tháng{" "}
                <span className="handwritten">{month}</span> năm{" "}
                <span className="handwritten">{year}</span>
              </div>
            </div>
            <div className="info-right">
              <div>Ký hiệu: DT/BN</div>
              <div>Số: {receipt.soPhieu.split("-")[1]}</div>
            </div>
          </div>

          <div className="table-container">
            <table className="items-table">
              <thead>
                <tr>
                  <th className="code-col">Mã vt</th>
                  <th className="name-col">Tên Mặt Hàng</th>
                  <th className="unit-col">Đơn vị tính</th>
                  <th className="qty-col">Số lượng</th>
                  {id && action === "edit" && (
                    <th className="action-col">Thao tác</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortItemsByUnit(receipt.items).map((item, index) => {
                  const isHighlighted = highlightMatchingItems(item, index);
                  const isCurrentHighlight =
                    highlightedItems.length > 0 &&
                    highlightedItems[currentHighlightIndex]?.index === index;
                  const hasQuantity =
                    item.soLuong && parseFloat(item.soLuong) > 0;

                  return (
                    <tr key={`item-${index}`} data-item-index={index}>
                      <td
                        className={`code-cell ${
                          isHighlighted ? "highlight" : ""
                        } ${isCurrentHighlight ? "current-highlight" : ""}`}
                      >
                        {item.maVt}
                      </td>
                      <td
                        className={`name-cell ${
                          isHighlighted ? "highlight" : ""
                        } ${isCurrentHighlight ? "current-highlight" : ""}`}
                      >
                        {item.ten}
                      </td>
                      <td
                        className={`unit-cell ${
                          isHighlighted ? "highlight" : ""
                        } ${isCurrentHighlight ? "current-highlight" : ""}`}
                      >
                        {item.dvt}
                      </td>
                      <td className="handwritten qty-cell">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          inputMode="decimal"
                          value={item.soLuong || ""}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const newItems = [...receipt.items];
                            // Find the actual index in the original unsorted array
                            const actualIndex = receipt.items.findIndex(
                              (originalItem) =>
                                originalItem.maVt === item.maVt &&
                                originalItem.ten === item.ten
                            );
                            if (actualIndex !== -1) {
                              newItems[actualIndex] = {
                                ...newItems[actualIndex],
                                soLuong: newValue,
                              };
                              setReceipt({ ...receipt, items: newItems });
                            }
                          }}
                          className={`qty-input ${
                            isHighlighted ? "highlight" : ""
                          } ${isCurrentHighlight ? "current-highlight" : ""}`}
                          title="Số lượng ban đầu"
                        />
                      </td>
                      {id && action === "edit" && (
                        <td className="action-cell">
                          {hasQuantity && (
                            <DeleteIcon
                              onClick={() => {
                                const actualIndex = receipt.items.findIndex(
                                  (originalItem) =>
                                    originalItem.maVt === item.maVt &&
                                    originalItem.ten === item.ten
                                );
                                if (actualIndex !== -1) {
                                  handleDeleteProduct(actualIndex);
                                }
                              }}
                              sx={{
                                cursor: "pointer",
                                transition: "color 0.2s ease",
                                "&:hover": {
                                  color: "red",
                                },
                              }}
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Add custom product button - mobile optimized */}
            <Box
              sx={{
                mt: 2,
                textAlign: "center",
                "@media print": { display: "none" },
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
                size="small"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  padding: { xs: "6px 12px", sm: "8px 16px" },
                }}
              >
                Thêm sản phẩm
              </Button>
            </Box>
          </div>

          <div className="total-section">
            <div className="status-row">
              <div className="status-item">
                <strong>Trạng thái: </strong>
                <span
                  className={`status-badge ${getReceiptStatus()
                    .toLowerCase()
                    .replace(/\s/g, "-")}`}
                >
                  {getReceiptStatus()}
                </span>
              </div>
              <div className="rdb-item">
                <strong>
                  <span className="handwritten">RDB - Rổ Đựng bánh</span>
                </strong>
              </div>
            </div>
          </div>

          <div className="signature-section">
            {/* Only show BQD approval button for admin users */}
            {isAdmin && (
              <div className="signature-box">
                <div className="signature-title">
                  Kiểm soát BQD
                  <br />
                  (Ký, ghi rõ họ tên)
                </div>
                <button
                  className="confirm-btn"
                  onClick={(e) =>
                    confirmAction(e.target, "Đã kiểm soát BQD", true)
                  }
                >
                  Xác nhận kiểm soát
                </button>
                <div className="status-text"></div>
              </div>
            )}

            <div className="signature-box">
              <div className="signature-title">Điều phối</div>
              <button
                className="confirm-btn"
                onClick={(e) =>
                  confirmAction(e.target, "Đã xác nhận gửi phiếu")
                }
              >
                Xác nhận gửi phiếu
              </button>
              <div className="status-text"></div>
            </div>
          </div>
        </div>
      )}

      {/* Show message when no store selected for new receipts */}
      {!storeId && !id && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 2,
          }}
        >
          <StorefrontIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Chọn cửa hàng để bắt đầu
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vui lòng chọn cửa hàng từ danh sách ở trên để tạo phiếu nhập mới
          </Typography>
        </Box>
      )}

      {/* Dialog for adding a new product - mobile optimized */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={window.innerWidth < 600}
        sx={{
          "& .MuiDialog-paper": {
            margin: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "auto" },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
          Thêm sản phẩm mới
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Category Selection */}
            <FormControl
              fullWidth
              margin="normal"
              size="small"
              error={!!formErrors.category}
            >
              <InputLabel>Danh mục sản phẩm</InputLabel>
              <Select
                value={selectedCategory}
                label="Danh mục sản phẩm"
                onChange={handleCategoryChange}
                disabled={loadingCategories}
              >
                <MenuItem value="">
                  <em>-- Chọn danh mục --</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.category}
                </Typography>
              )}
            </FormControl>

            {/* Product Selection */}
            <FormControl
              fullWidth
              margin="normal"
              size="small"
              error={!!formErrors.product}
              disabled={!selectedCategory}
            >
              <InputLabel>Sản phẩm</InputLabel>
              <Select
                value={selectedProduct}
                label="Sản phẩm"
                onChange={handleProductChange}
                disabled={!selectedCategory || loadingProducts}
              >
                <MenuItem value="">
                  <em>-- Chọn sản phẩm --</em>
                </MenuItem>
                {selectedCategory &&
                  products[selectedCategory]?.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name} {product.unit && `(${product.unit})`}
                    </MenuItem>
                  ))}
              </Select>
              {formErrors.product && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.product}
                </Typography>
              )}
            </FormControl>

            {/* Product Info Display */}
            {selectedProduct && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Mã sản phẩm:</strong> {newProduct.maVt}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Tên sản phẩm:</strong> {newProduct.ten}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Đơn vị tính:</strong> {newProduct.dvt}
                </Typography>
              </Box>
            )}

            {/* Only show Số lượng field in NewPhieuNhapBM (SL Thực nhận is always hidden) */}
            <TextField
              fullWidth
              label="Số lượng"
              name="soLuong"
              value={newProduct.soLuong}
              onChange={handleNewProductChange}
              error={!!formErrors.soLuong}
              helperText={formErrors.soLuong}
              type="number"
              inputMode="decimal"
              size="small"
              sx={{ mt: 2 }}
              inputProps={{ min: 0, step: 0.1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1 } }}>
          <Button onClick={handleCloseAddDialog} color="inherit">
            Hủy bỏ
          </Button>
          <Button
            onClick={handleAddNewProduct}
            variant="contained"
            color="primary"
            disabled={!selectedProduct}
          >
            Thêm sản phẩm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={openPreviewDialog}
        onClose={handleClosePreview}
        maxWidth="xl"
        fullWidth
        fullScreen={window.innerWidth < 900}
        sx={{
          "& .MuiDialog-paper": {
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: "100%", sm: "90vh" },
          },
        }}
      >
        <DialogTitle sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0"
        }}>
          <Typography variant="h6">Xem trước phiếu nhập</Typography>
          <Box>
            <Button
              onClick={handleSavePreview}
              variant="contained"
              color="primary"
              size="small"
              sx={{ mr: 1 }}
            >
              Lưu
            </Button>
            <Button
              onClick={handleClosePreview}
              variant="outlined"
              size="small"
            >
              Đóng
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 2 }, w: "100%" }}>
          <div className="receipt preview-mode">
            <div className="header">
              <div className="logo">
                DOTI
                <br />
                COM
              </div>
              <div className="title">PHIẾU GIAO NHẬN HÀNG HÓA</div>
              <div className="subtitle">20 20 30 90 96</div>
            </div>

            <div className="info-section">
              <div className="info-left">
                <div className="customer-row">
                  <span className="customer-label">Khách hàng:</span>
                  <div className="customer-display handwritten">
                    {receipt?.khachHang || storeName || "Chưa chọn cửa hàng"}
                  </div>
                </div>
                <div className="date-row">
                  Ngày: <span className="handwritten">{day}</span> tháng{" "}
                  <span className="handwritten">{month}</span> năm{" "}
                  <span className="handwritten">{year}</span>
                </div>
              </div>
              <div className="info-right">
                <div>Ký hiệu: DT/BN</div>
                <div>Số: {receipt?.soPhieu?.split("-")[1]}</div>
              </div>
            </div>

            <div className="table-container">
              <table className="items-table">
                <thead>
                  <tr>
                    {window.innerWidth <= 768 ? (
                      <>
                        <th className="code-col">Mã vt</th>
                        <th className="name-col">Tên Mặt Hàng</th>
                        <th className="unit-col">Đơn vị tính</th>
                        <th className="qty-col">Số lượng</th>
                      </>
                    ) : (
                      organizePreviewItemsIntoColumns().map((_, colIndex) => (
                        <React.Fragment key={`header-${colIndex}`}>
                          <th className="code-col">Mã vt</th>
                          <th className="name-col">Tên Mặt Hàng</th>
                          <th className="unit-col">Đơn vị tính</th>
                          <th className="qty-col">Số lượng</th>
                        </React.Fragment>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {window.innerWidth <= 768 ? (
                    // Mobile layout
                    organizePreviewItemsIntoColumns()[0]?.map((item, rowIndex) => (
                      <tr key={`preview-mobile-row-${rowIndex}`}>
                        <td>{item.maVt}</td>
                        <td>{item.ten}</td>
                        <td className="unit-cell">{item.dvt}</td>
                        <td className="handwritten qty-cell">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            inputMode="decimal"
                            value={item.soLuong || ""}
                            onChange={(e) =>
                              handlePreviewItemChange(rowIndex, "soLuong", e.target.value)
                            }
                            className="qty-input"
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    // Desktop layout
                    Array.from({
                      length: organizePreviewItemsIntoColumns()[0]?.length || 0,
                    }).map((_, rowIndex) => (
                      <tr key={`preview-row-${rowIndex}`}>
                        {organizePreviewItemsIntoColumns().map((column, colIndex) => {
                          const item = column[rowIndex];
                          if (!item) return null;

                          const originalIndex =
                            colIndex * (organizePreviewItemsIntoColumns()[0]?.length || 0) +
                            rowIndex;

                          return (
                            <React.Fragment key={`preview-item-${rowIndex}-${colIndex}`}>
                              <td>{item.maVt}</td>
                              <td>{item.ten}</td>
                              <td className="unit-cell">{item.dvt}</td>
                              <td className="handwritten qty-cell">
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  inputMode="decimal"
                                  value={item.soLuong || ""}
                                  onChange={(e) =>
                                    handlePreviewItemChange(
                                      originalIndex,
                                      "soLuong",
                                      e.target.value
                                    )
                                  }
                                  className="qty-input"
                                />
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    )))
                  }
                </tbody>
              </table>
            </div>

            <div className="total-section">
              <div className="status-row">
                <div className="status-item">
                  <strong>Trạng thái: </strong>
                  <span className="status-badge">
                    Xem trước
                  </span>
                </div>
                <div className="rdb-item">
                  <strong>
                    <span className="handwritten">RDB - Rổ Đựng bánh</span>
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={handleClosePreview} variant="outlined">
            Đóng
          </Button>
          <Button
            onClick={handleSavePreview}
            variant="contained"
            color="primary"
          >
            Lưu thay đổi
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Nút cuộn xuống cuối trang (floating button) */}
     
      {showScrollToBottom && (
        <Box
          sx={{
            position: "fixed",
            right: 20,
            bottom: 80,
            zIndex: 1300,

            "@media print": { display: "none" },
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            sx={{
              borderRadius: "50%",
              minWidth: 0,
              width: 48,
              height: 48,
              fontSize: 28,
              boxShadow: 3,
              p: 0,
            }}
            onClick={() => {
              if (bottomRef.current) {
                bottomRef.current.scrollIntoView({ behavior: "smooth" });
              } else {
                window.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: "smooth",
                });
              }
            }}
            title="Cuộn xuống cuối trang"
          >
            ↓
          </Button>
        </Box>
      )}

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
          background-color: #f5f5f5;
          padding: 8px;
          overflow-x: hidden;
        }

        /* Hide number input spinners and prevent wheel changes */
        input[type="number"] {
          -webkit-appearance: textfield;
          -moz-appearance: textfield;
        }

        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        input[type="number"]::-moz-inner-spin-button,
        input[type="number"]::-moz-outer-spin-button {
          -moz-appearance: none;
          margin: 0;
        }

        /* Prevent accidental wheel changes */
        input[type="number"]:focus {
          outline: 2px solid #2196f3;
          outline-offset: 1px;
        }

        .receipt {
          max-width: 100%;
          margin: 0 auto;
          background: white;
          padding: 12px;
          border: 2px solid #333;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
          width: 100%;
          overflow-x: hidden;
        }

        .header {
          text-align: center;
          margin-bottom: 15px;
          position: relative;
        }

        .logo {
          position: absolute;
          left: 0;
          top: 0;
          width: 50px;
          height: 50px;
          background: linear-gradient(45deg, #ff6b35, #f7931e);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 10px;
        }

        .title {
          color: #d32f2f;
          font-size: 18px;
          font-weight: bold;
          margin-top: 10px;
          letter-spacing: 1px;
          line-height: 1.2;
        }

        .subtitle {
          font-style: italic;
          color: #666;
          margin-top: 5px;
          font-size: 12px;
        }

        .info-section {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 15px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
          font-size: 12px;
        }

        .info-left {
          flex: 1;
        }

        .info-right {
          text-align: right;
          align-self: flex-end;
        }

        .customer-row {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-bottom: 8px;
        }

        .customer-label {
          font-weight: bold;
        }

        .date-row {
          font-size: 11px;
        }

        .table-container {
          overflow-x: hidden;
          margin-bottom: 20px;
          width: 100%;
          -webkit-overflow-scrolling: auto;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px; /* Increased from 10px */
          table-layout: fixed;
          max-width: 100%;
        }

        .items-table th,
        .items-table td {
          border: 1px solid #333;
          padding: 8px 4px; /* Increased padding from 6px to 8px */
          text-align: left;
          word-wrap: break-word;
          vertical-align: middle;
          overflow: hidden;
          transition: all 0.2s ease;
        }

        /* Add thick borders between column groups */
        .items-table th:nth-child(4n),
        .items-table td:nth-child(4n) {
          border-right: 3px solid #333;
          padding-right: 8px;
        }

        /* Add spacing to the left of each new column group */
        .items-table th:nth-child(4n+1):not(:first-child),
        .items-table td:nth-child(4n+1):not(:first-child) {
          padding-left: 8px;
        }

        .items-table th:first-child,
        .items-table td:first-child {
          border-left: 3px solid #333;
        }

        /* Add thick top and bottom borders for table */
        .items-table thead tr:first-child th {
          border-top: 3px solid #333;
        }

        .items-table tbody tr:last-child td {
          border-bottom: 3px solid #333;
        }

        /* Add hover effects for table rows */
        .items-table tbody tr {
          transition: all 0.2s ease;
        }

        .items-table tbody tr:hover {
          background-color: rgb(208, 229, 248);
        }

        .items-table tbody tr:hover td {
          border-color: #2196f3;
          box-shadow: 0 0 3px rgba(33, 150, 243, 0.3);
        }

        .items-table tbody tr:hover .qty-input {
          border-bottom-color: #2196f3;
          background-color: rgba(33, 150, 243, 0.05);
        }

        /* Enhanced hover for highlighted items */
        .items-table tbody tr.highlight:hover td {
          border-color: #ff9800;
          box-shadow: 0 0 5px rgba(255, 152, 0, 0.5);
        }

        .items-table tbody tr.current-highlight:hover td {
          border-color: #ff5722;
          box-shadow: 0 0 8px rgba(255, 87, 34, 0.6);
        }

        .code-col {
          width: 15%; /* Reduced from 18% */
          min-width: 0;
        }

        .name-col {
          width: 52%; /* Reduced from 67% */
          min-width: 0;
        }

        .unit-col {
          width: 18%; /* New column */
          min-width: 0;
          text-align: center;
        }

        .qty-col {
          width: 15%; /* Same as before */
          min-width: 0;
          text-align: center;
        }

        .action-col {
          width: 8%;
          min-width: 0;
          text-align: center;
        }

        .code-cell {
          font-size: 15px; /* Increased from 9px */
          word-break: break-all;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }

        .name-cell {
          font-size: 18px; /* Increased from 9px */
          line-height: 1.2;
          word-break: break-word;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .unit-cell {
          font-size: 14px;
          text-align: center;
          word-break: break-word;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .qty-cell {
          text-align: center;
        }

        .action-cell {
          text-align: center;
          vertical-align: middle;
        }

        .delete-btn {
          background: #ff4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          cursor: pointer;
          font-size: 16px;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          line-height: 1;
        }

        .delete-btn:hover {
          background: #cc0000;
          transform: scale(1.1);
        }

        .delete-btn:active {
          transform: scale(0.95);
        }

        .handwritten {
          font-family: "Courier New", monospace;
          color: #0066cc;
          font-weight: bold;
        }

        .customer-display {
          border: none;
          border-bottom: 1px dashed #999;
          padding: 4px 8px;
          width: 100%;
          margin-top: 4px;
          font-size: 12px;
          min-height: 24px;
          background-color: #f8f9fa;
          color: #0066cc;
          font-weight: bold;
          font-family: "Courier New", monospace;
        }

        .customer-input {
          border: none;
          border-bottom: 1px dashed #999;
          outline: none;
          padding: 4px 8px;
          width: 100%;
          margin-top: 4px;
          font-size: 12px;
        }

        .signature-section {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: 20px;
          padding-top: 15px;
          border-top: 2px solid #333;
        }

        .signature-box {
          text-align: center;
          padding: 15px 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background-color: #f9f9f9;
        }

        .signature-title {
          font-weight: bold;
          margin-bottom: 12px;
          color: #333;
          font-size: 11px;
          line-height: 1.3;
        }

        .confirm-btn {
          background: linear-gradient(45deg, #4caf50, #45a049);
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 25px;
          cursor: pointer;
          font-size: 12px;
          font-weight: bold;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          width: 100%;
          min-height: 44px;
          touch-action: manipulation;
        }

        .confirm-btn:hover {
          background: linear-gradient(45deg, #45a049, #4caf50);
          transform: translateY(-1px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
        }

        .confirm-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .confirm-btn.confirmed {
          background: linear-gradient(45deg, #2196f3, #1976d2);
          cursor: default;
        }

        .confirm-btn.confirmed:hover {
          transform: none;
          background: linear-gradient(45deg, #2196f3, #1976d2);
        }

        .status-text {
          font-size: 10px;
          margin-top: 8px;
          color: #666;
          font-style: italic;
          line-height: 1.2;
        }

        .total-section {
          margin-top: 15px;
          padding: 10px;
          background-color: #f0f0f0;
          border-radius: 5px;
        }

        .status-row {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: flex-start;
        }

        .status-item {
          font-size: 11px;
        }

        .rdb-item {
          font-size: 11px;
          align-self: flex-end;
        }

        .highlight {
          background-color: #fffde7;
          box-shadow: 0 0 5px rgba(255, 193, 7, 0.5);
          font-weight: bold;
        }

        .current-highlight {
          background-color: #fff3c4 !important;
          box-shadow: 0 0 10px rgba(255, 152, 0, 0.8) !important;
          border: 2px solid #ff9800 !important;
        }

        .scroll-highlight {
          animation: pulseHighlight 1s ease-in-out;
        }

        @keyframes pulseHighlight {
          0% {
            transform: scale(1);
            box-shadow: 0 0 10px rgba(255, 152, 0, 0.8);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 0 20px rgba(255, 152, 0, 1);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 10px rgba(255, 152, 0, 0.8);
          }
        }

        input.highlight {
          background-color: #fffde7;
          border-bottom: 2px solid #ffc107 !important;
        }

        input.current-highlight {
          background-color: #fff3c4 !important;
          border-bottom: 2px solid #ff9800 !important;
        }

        /* Very small mobile screens (width <= 360px) */
        @media (max-width: 360px) {
          body {
            padding: 4px;
            overflow-x: hidden;
          }

          .receipt {
            padding: 8px;
            border-width: 1px;
            overflow-x: hidden;
          }

          .logo {
            width: 40px;
            height: 40px;
            font-size: 8px;
          }

          .title {
            font-size: 14px;
            letter-spacing: 0.5px;
            margin-left: 45px;
          }

          .subtitle {
            font-size: 10px;
          }

          .info-section {
            font-size: 10px;
            margin-bottom: 12px;
            padding-bottom: 8px;
            gap: 6px;
          }

          .customer-row {
            gap: 3px;
            margin-bottom: 6px;
          }

          .customer-input {
            font-size: 10px;
            padding: 3px 6px;
          }

          .date-row {
            font-size: 9px;
          }

          .info-right {
            font-size: 10px;
          }

          .table-container {
            margin-bottom: 15px;
            overflow-x: hidden;
          }

          .items-table {
            font-size: 14px; /* Increased from 8px */
            table-layout: fixed;
            width: 100%;
          }

          .items-table th,
          .items-table td {
            padding: 4px 2px;
            overflow: hidden;
          }

          .code-col {
            width: 8%;
          }

          .name-col {
            width: 28%; /* Reduced to accommodate action column */
          }

          .unit-col {
            width: 12%; /* Reduced for mobile */
          }

          .qty-col {
            width: 40%; /* Reduced from 45% */
          }

          .action-col {
            width: 12%;
          }

          .delete-btn {
            width: 20px;
            height: 20px;
            font-size: 14px;
          }

          .signature-section {
            gap: 10px;
            margin-top: 15px;
            padding-top: 12px;
          }

          .signature-box {
            padding: 10px 6px;
          }

          .signature-title {
            font-size: 9px;
            margin-bottom: 8px;
            line-height: 1.2;
          }

          .confirm-btn {
            font-size: 10px;
            padding: 10px 12px;
            min-height: 40px;
          }

          .status-text {
            font-size: 8px;
            margin-top: 6px;
          }

          .total-section {
            margin-top: 12px;
            padding: 8px;
          }

          .status-item,
          .rdb-item {
            font-size: 9px;
          }

          .status-badge {
            font-size: 8px;
            padding: 2px 6px;
            border-radius: 8px;
          }
        }

        /* Tablet styles */
        @media (min-width: 768px) {
          body {
            padding: 20px;
            overflow-x: hidden;
          }

          .receipt {
            padding: 20px;
            overflow-x: hidden;
          }

          .logo {
            width: 60px;
            height: 60px;
            font-size: 12px;
          }

          .title {
            font-size: 24px;
          }

          .subtitle {
            font-size: 14px;
          }

          .info-section {
            flex-direction: row;
            justify-content: space-between;
            font-size: 14px;
          }

          .customer-row {
            flex-direction: row;
            align-items: center;
            gap: 8px;
          }

          .table-container {
            overflow-x: hidden;
          }

          .code-col {
            width: 15%; /* Reduced from 18% */
          }

          .name-col {
            width: 47%; /* Reduced to accommodate action column */
          }

          .unit-col {
            width: 18%; /* New column */
          }

          .qty-col {
            width: 15%; /* Same as before */
          }

          .action-col {
            width: 5%;
          }

          .signature-section {
            flex-direction: row;
            justify-content: space-around;
          }

          .signature-title {
            font-size: 13px;
          }

          .confirm-btn {
            font-size: 14px;
          }

          .status-row {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }

          .status-item,
          .rdb-item {
            font-size: 13px;
          }

          /* Maintain thick borders and spacing on tablet */
          .items-table th:nth-child(4n),
          .items-table td:nth-child(4n) {
            border-right: 3px solid #333;
            padding-right: 8px;
          }

          .items-table th:nth-child(4n+1):not(:first-child),
          .items-table td:nth-child(4n+1):not(:first-child) {
            padding-left: 8px;
          }

          .items-table th:first-child,
          .items-table td:first-child {
            border-left: 3px solid #333;
          }
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          .receipt {
            max-width: 1200px;
            overflow-x: hidden;
          }

          .title {
            font-size: 28px;
          }

          .items-table {
            font-size: 14px; /* Increased from 12px */
          }

          .signature-section {
            gap: 20px;
          }

          /* Maintain thick borders and spacing on desktop */
          .items-table th:nth-child(4n),
          .items-table td:nth-child(4n) {
            border-right: 3px solid #333;
            padding-right: 8px;
          }

          .items-table th:nth-child(4n+1):not(:first-child),
          .items-table td:nth-child(4n+1):not(:first-child) {
            padding-left: 8px;
          }

          .items-table th:first-child,
          .items-table td:first-child {
            border-left: 3px solid #333;
          }
        }

        @media print {
          body {
            padding: 0;
            overflow-x: hidden;
          }
          .receipt {
            max-width: 100%;
            border: none;
            box-shadow: none;
            padding: 10px;
            overflow-x: hidden;
          }
          .qty-input {
            border-bottom: 1px solid #999;
          }
          .signature-section {
            flex-direction: row;
          }
          .table-container {
            overflow-x: hidden;
          }
          .action-col,
          .action-cell,
          .delete-btn {
            display: none !important;
          }
        }

        /* Global prevent horizontal scroll */
        html,
        body {
          overflow-x: hidden;
          max-width: 100vw;
        }

        * {
          box-sizing: border-box;
        }

        .MuiContainer-root {
          overflow-x: hidden !important;
        }

        .preview-mode {
          border: none;
          box-shadow: none;
        }

        .preview-mode .signature-section {
          display: none;
        }
      `}</style>
    </Box>
  );
};

export default NewPhieuNhapBM;
